/**
 * Frontend mirror of the backend `_compute_brand_badges_discount` logic.
 * Used to preview the auto-applied per-brand badge discounts in the basket
 * before the customer hits "Place order" — backend remains the source of truth.
 */

/**
 * True if `badge`'s optional schedule (campaign date range + weekly + daily
 * hour window) currently allows it to apply. Empty fields = "always".
 * Day-of-week + hour are evaluated in Europe/London (the kitchen's TZ).
 */
export const badgeWithinWindow = (badge, nowDate = new Date()) => {
    if (!badge) return false;
    const nowMs = nowDate.getTime();

    if (badge.starts_at) {
        const s = new Date(badge.starts_at).getTime();
        if (!Number.isNaN(s) && nowMs < s) return false;
    }
    if (badge.ends_at) {
        const e = new Date(badge.ends_at).getTime();
        if (!Number.isNaN(e) && nowMs > e) return false;
    }

    const days = badge.days_of_week || [];
    const startH = badge.start_hour;
    const endH = badge.end_hour;
    const needsLocal = days.length > 0 || startH !== null && startH !== undefined || endH !== null && endH !== undefined;
    if (needsLocal) {
        // Format current time in Europe/London → derive weekday + hour + minute
        const fmt = new Intl.DateTimeFormat("en-GB", {
            timeZone: "Europe/London",
            weekday: "short",
            hour: "numeric", minute: "numeric", hour12: false,
        }).formatToParts(nowDate);
        const partMap = Object.fromEntries(fmt.map((p) => [p.type, p.value]));
        const wkMap = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
        const weekday = wkMap[partMap.weekday] ?? 0;
        const hour = Number(partMap.hour) + Number(partMap.minute) / 60.0;
        if (days.length > 0 && !days.includes(weekday)) return false;
        if (startH !== null && startH !== undefined || endH !== null && endH !== undefined) {
            const sh = startH != null ? Number(startH) : 0;
            const eh = endH != null ? Number(endH) : 24;
            if (sh <= eh) {
                if (!(sh <= hour && hour < eh)) return false;
            } else {
                if (!(hour >= sh || hour < eh)) return false;
            }
        }
    }
    return true;
};

export const computeBrandBadges = ({ lines, brands, orderType }) => {
    if (!lines?.length || !brands?.length) {
        return { discount: 0, freeDelivery: false, applied: [] };
    }
    // Per-brand subtotal
    const brandSub = {};
    for (const l of lines) {
        brandSub[l.brand_id] = (brandSub[l.brand_id] || 0) + l.price * l.qty;
    }
    const brandById = Object.fromEntries(brands.map((b) => [b.id, b]));
    let discount = 0;
    let freeDelivery = false;
    const applied = [];
    for (const [bid, sub] of Object.entries(brandSub)) {
        const brand = brandById[bid];
        if (!brand?.badges?.length) continue;
        for (const badge of brand.badges) {
            if (badge.active === false) continue;
            // Order-type gate — match the backend filter
            const appliesTo = (badge.applies_to || "both").toLowerCase();
            if (appliesTo !== "both" && appliesTo !== orderType) continue;
            // Schedule gate — campaign window + weekly + hourly. Empty = always.
            if (!badgeWithinWindow(badge)) continue;
            const minSpend = Number(badge.min_spend || 0);
            if (sub + 1e-9 < minSpend) continue;
            const value = Number(badge.value || 0);
            const entry = {
                brand_id: bid,
                brand_name: brand.name,
                brand_token: brand.token,
                brand_accent: brand.accent,
                badge_type: badge.type,
                label: badge.label || "",
                value,
                min_spend: minSpend,
                amount: 0,
                display: "",
            };
            if (badge.type === "pct_off") {
                entry.amount = Math.round(sub * value) / 100;
                entry.display = badge.label || `${value}% off`;
                discount += entry.amount;
            } else if (badge.type === "amount_off") {
                entry.amount = Math.round(Math.min(value, sub) * 100) / 100;
                entry.display = badge.label || `£${value.toFixed(2)} off`;
                discount += entry.amount;
            } else if (badge.type === "free_delivery" && orderType === "delivery") {
                freeDelivery = true;
                entry.display = badge.label || "Free delivery";
            } else if (badge.type === "marketing") {
                continue;
            } else {
                continue;
            }
            applied.push(entry);
        }
    }
    return {
        discount: Math.round(discount * 100) / 100,
        freeDelivery,
        applied,
    };
};
